import React from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Content Service for fetching dynamic content from Firebase
 */

// Get content for a specific page
export const getPageContent = async (pageName) => {
  try {
    const pageDocRef = doc(db, 'yclab-content', pageName);
    const pageDocSnap = await getDoc(pageDocRef);
    
    if (pageDocSnap.exists()) {
      const pageData = pageDocSnap.data();
      return {
        success: true,
        data: pageData.sections || [],
        metadata: {
          lastUpdated: pageData.lastUpdated,
          pageTitle: pageData.pageTitle
        }
      };
    } else {
      return {
        success: false,
        error: 'Page not found',
        data: []
      };
    }
  } catch (error) {
    console.error(`Error fetching content for ${pageName}:`, error);
    return {
      success: false,
      error: error.message,
      data: []
    };
  }
};

// Get content by section ID
export const getSectionContent = (sections, sectionId) => {
  // Handle both array and object with sections property
  const sectionsArray = Array.isArray(sections) ? sections : (sections?.sections || []);
  return sectionsArray.find(section => section.id === sectionId) || null;
};

// Get sections by type
export const getSectionsByType = (sections, type) => {
  // Handle both array and object with sections property
  const sectionsArray = Array.isArray(sections) ? sections : (sections?.sections || []);
  return sectionsArray.filter(section => section.type === type && section.visible !== false);
};

// Get all visible sections ordered
export const getVisibleSections = (sections) => {
  // Handle both array and object with sections property
  const sectionsArray = Array.isArray(sections) ? sections : (sections?.sections || []);
  return sectionsArray
    .filter(section => section.visible !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
};

// Content hook for React components with minimum loading time
export const usePageContent = (pageName, minLoadingTime = 800) => {
  const [content, setContent] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  
  React.useEffect(() => {
    const fetchContent = async () => {
      setLoading(true);
      setError(null);
      
      const startTime = Date.now();
      
      try {
        const pageDocRef = doc(db, 'yclab-content', pageName);
        const pageDocSnap = await getDoc(pageDocRef);
        
        if (pageDocSnap.exists()) {
          const pageData = pageDocSnap.data();
          setContent({
            sections: pageData.sections || [],
            lastUpdated: pageData.lastUpdated,
            pageTitle: pageData.pageTitle
          });
          setError(null);
        } else {
          setError('Page content not found');
          setContent(null);
        }
      } catch (err) {
        console.error(`Error fetching content for ${pageName}:`, err);
        setError(err.message);
        setContent(null);
      }
      
      // Ensure minimum loading time for better UX
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
      
      setTimeout(() => {
        setLoading(false);
      }, remainingTime);
    };
    
    if (pageName) {
      fetchContent();
    }
  }, [pageName, minLoadingTime]);
  
  return { content, loading, error };
};

// Default/fallback content structure
export const getDefaultContent = (sectionType) => {
  const defaults = {
    hero: {
      content: {
        mainTitle: 'Driving Youth Employment and inclusive economic development across Africa',
        subtitle: '',
        primaryButton: { text: 'What we do', link: '#what-we-do' },
        secondaryButton: { text: 'Play Video', link: '#video', hasIcon: true },
        backgroundImage: '/assets/hero-bg.jpg'
      }
    },
    pillars: {
      content: {
        sectionLabel: 'WHAT WE DO',
        mainTitle: 'We center our work around three interconnected pillars',
        pillars: []
      }
    },
    'impact-stories': {
      content: {
        sectionLabel: 'IMPACT STORIES',
        mainTitle: 'Real Stories, Real Impact',
        stories: []
      }
    }
  };
  
  return defaults[sectionType] || { content: {} };
};